/**
 * The closed `needs_attention_reason` enum (architecture §15.3). Every reason has an owner, a
 * customer message key with parameters, the actions allowed on it, and whether Paige retries by
 * itself. Screens never show a bare code; they render the message through the catalogue.
 */
export const reasonCodes = [
  'source_file_unreadable',
  'unsupported_format',
  'page_count_mismatch',
  'render_failed',
  'ocr_failed',
  'reader_refused',
  'reader_unavailable',
  'pool_quota_exhausted',
  'lease_expired_repeatedly',
  'ambiguous_unresolved',
  'destination_auth_failed',
  'destination_mapping_invalid',
  'destination_unreachable',
  'destination_ambiguous',
  'tenant_over_quota',
  'credits_exhausted',
  'tenant_paused',
  'sender_quarantined',
  'engine_stalled',
  'engine_unrecoverable',
  'engine_workspace_mismatch',
  'workspace_migration_failed',
  'workspace_restore_failed',
  'disk_attach_failed',
  'disk_full',
  'projection_lagging',
  'reader_generation_retired',
  'learning_gate_refused',
  'answer_truncated',
  'stage_timeout',
  'source_file_set_aside',
] as const;

export type ReasonCode = (typeof reasonCodes)[number];
export type ReasonOwner = 'customer' | 'support' | 'engineering';
export type AutoRetry = 'none' | 'backoff' | 'next_release';

/**
 * How a parked unit heals (alerts 2026-10-07; the same table as `ReasonHealingCatalog` in the domain):
 * `transient` a dependency of ours was away, it goes back by itself on a schedule; `ours` the same input
 * fails the same way on the same build, it goes back once on a newer build; `poison` it harms the worker,
 * set aside and engineering told; `theirs` the customer's to fix, it goes back to them in plain words and is
 * never staff's queue; `held` held on purpose until something a person controls clears.
 */
export type Heals = 'transient' | 'ours' | 'poison' | 'theirs' | 'held';

export type ReasonAction =
  | 'review'
  | 'confirm_split'
  | 'choose_type'
  | 'correct_field'
  | 'replace_file'
  | 'retry'
  | 'reconnect_destination'
  | 'fix_mapping'
  | 'choose_destination'
  | 'buy_credits'
  | 'resume'
  | 'release_sender'
  | 'contact_support'
  | 'wait'
  | 'skip';

export interface ReasonSpec {
  owner: ReasonOwner;
  /** Message key in the catalogue; parameters are named there. */
  message: `reason.${ReasonCode}`;
  /** The first entry is the one clearing action shown on the rail. */
  allowedActions: readonly ReasonAction[];
  autoRetry: AutoRetry;
  heals: Heals;
  informational?: boolean;
}

const spec = (
  owner: ReasonOwner,
  code: ReasonCode,
  allowedActions: readonly ReasonAction[],
  autoRetry: AutoRetry = 'none',
): ReasonSpec => ({
  owner,
  message: `reason.${code}`,
  allowedActions,
  autoRetry,
  heals: heals[code],
});

/** Each reason's healing class; kept in step with `ReasonHealingCatalog` (a domain test reads this file). */
export const heals: Record<ReasonCode, Heals> = {
  source_file_unreadable: 'theirs',
  unsupported_format: 'theirs',
  page_count_mismatch: 'ours',
  render_failed: 'ours',
  ocr_failed: 'ours',
  reader_refused: 'ours',
  reader_unavailable: 'transient',
  pool_quota_exhausted: 'transient',
  lease_expired_repeatedly: 'poison',
  ambiguous_unresolved: 'poison',
  destination_auth_failed: 'theirs',
  destination_mapping_invalid: 'theirs',
  destination_unreachable: 'transient',
  destination_ambiguous: 'ours',
  tenant_over_quota: 'held',
  credits_exhausted: 'held',
  tenant_paused: 'held',
  sender_quarantined: 'held',
  engine_stalled: 'transient',
  engine_unrecoverable: 'poison',
  engine_workspace_mismatch: 'ours',
  workspace_migration_failed: 'ours',
  workspace_restore_failed: 'transient',
  disk_attach_failed: 'transient',
  disk_full: 'transient',
  projection_lagging: 'transient',
  reader_generation_retired: 'ours',
  learning_gate_refused: 'held',
  answer_truncated: 'ours',
  stage_timeout: 'transient',
  source_file_set_aside: 'theirs',
};

export const reasons: Record<ReasonCode, ReasonSpec> = {
  source_file_unreadable: spec('customer', 'source_file_unreadable', ['replace_file', 'skip']),
  unsupported_format: spec('customer', 'unsupported_format', ['replace_file', 'skip']),
  page_count_mismatch: spec('customer', 'page_count_mismatch', ['review', 'replace_file']),
  render_failed: spec('support', 'render_failed', ['retry', 'contact_support'], 'backoff'),
  ocr_failed: spec('support', 'ocr_failed', ['retry', 'contact_support'], 'backoff'),
  reader_refused: spec('customer', 'reader_refused', ['review', 'skip']),
  reader_unavailable: spec('engineering', 'reader_unavailable', ['wait'], 'backoff'),
  pool_quota_exhausted: spec('engineering', 'pool_quota_exhausted', ['wait'], 'backoff'),
  lease_expired_repeatedly: spec('engineering', 'lease_expired_repeatedly', ['contact_support']),
  ambiguous_unresolved: spec('customer', 'ambiguous_unresolved', ['confirm_split', 'choose_type']),
  destination_auth_failed: spec('customer', 'destination_auth_failed', ['reconnect_destination']),
  destination_mapping_invalid: spec('customer', 'destination_mapping_invalid', ['fix_mapping']),
  destination_unreachable: spec('customer', 'destination_unreachable', ['retry'], 'backoff'),
  destination_ambiguous: spec('customer', 'destination_ambiguous', ['choose_destination']),
  tenant_over_quota: spec('customer', 'tenant_over_quota', ['buy_credits', 'contact_support']),
  credits_exhausted: spec('customer', 'credits_exhausted', ['buy_credits']),
  tenant_paused: spec('customer', 'tenant_paused', ['resume']),
  sender_quarantined: spec('customer', 'sender_quarantined', ['release_sender']),
  engine_stalled: spec('engineering', 'engine_stalled', ['wait'], 'backoff'),
  engine_unrecoverable: spec('engineering', 'engine_unrecoverable', ['contact_support']),
  engine_workspace_mismatch: spec('engineering', 'engine_workspace_mismatch', ['contact_support']),
  workspace_migration_failed: spec('engineering', 'workspace_migration_failed', ['wait']),
  workspace_restore_failed: spec('engineering', 'workspace_restore_failed', ['wait']),
  disk_attach_failed: spec('engineering', 'disk_attach_failed', ['wait'], 'backoff'),
  disk_full: spec('engineering', 'disk_full', ['wait']),
  projection_lagging: spec('engineering', 'projection_lagging', ['wait'], 'backoff'),
  reader_generation_retired: spec(
    'support',
    'reader_generation_retired',
    ['retry'],
    'next_release',
  ),
  learning_gate_refused: {
    ...spec('customer', 'learning_gate_refused', ['review']),
    informational: true,
  },
  answer_truncated: spec(
    'support',
    'answer_truncated',
    ['retry', 'skip', 'contact_support'],
    'backoff',
  ),
  stage_timeout: spec('engineering', 'stage_timeout', ['wait'], 'backoff'),
  source_file_set_aside: spec('customer', 'source_file_set_aside', ['replace_file', 'skip']),
};

export function isReasonCode(value: string): value is ReasonCode {
  return (reasonCodes as readonly string[]).includes(value);
}
